import { ProductDocument } from '@util/types/firestore/products';
import { onSnapshot } from 'firebase/firestore';
import { useMemo, useState, useEffect } from 'react';
import { getProductsByIdQuery } from '@util/firestore/products';
import { logError } from '@util/logError';
import {
  CART_TELEMETRY_EVENTS,
  emitCartTelemetry,
} from '@util/cartTelemetry';

/**
 * Firestore's `in` operator accepts at most 30 values per query. A cart with
 * more than 30 distinct product ids used to throw inside the single query and,
 * because the error was swallowed by a bare console.warn, wedged the product
 * list at its last value forever (GB #1323, mechanism M3). We chunk the ids
 * into groups of <=30 and open one realtime subscription per chunk, then merge
 * the per-chunk results into a single list.
 */
const FIRESTORE_IN_LIMIT = 30;

function chunk<T>(arr: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < arr.length; i += size) {
    out.push(arr.slice(i, i + size));
  }
  return out;
}

const useRealTimeProducts = (ids: string[]) => {
  const [realTimeProducts, setProducts] = useState<ProductDocument[]>([]);
  /**
   * The set of ids belonging to chunks that have delivered at least one
   * SUCCESSFUL snapshot. A chunk that errored is deliberately excluded, so a
   * caller can distinguish "still loading / errored" from "confirmed absent"
   * (a product present in a resolved chunk's query but missing from the
   * results is genuinely deleted). Additive output consumed by later work
   * (GB #1323 Fix A); safe to ignore.
   */
  const [resolvedIds, setResolvedIds] = useState<Set<string>>(new Set());
  /**
   * The most recent snapshot error, or null when all chunks are healthy.
   * Lets the drawer render a degraded-but-honest state instead of blanking.
   */
  const [subscriptionError, setSubscriptionError] = useState<Error | null>(
    null
  );

  // Stable key so cart-object identity churn (a new array with the SAME ids on
  // every calcCart pass) does not tear down and recreate the subscriptions.
  const idsKey = ids.join(',');

  const chunks = useMemo(
    () => (ids.length ? chunk(ids, FIRESTORE_IN_LIMIT) : []),
    // Intentionally keyed on the joined string, not the array identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [idsKey]
  );

  useEffect(() => {
    if (chunks.length === 0) {
      setProducts([]);
      setResolvedIds(new Set());
      setSubscriptionError(null);
      return;
    }

    // Resubscribing (idsKey changed): the previous chunk maps are gone, so any
    // error/resolved state they produced is now stale. Clear them so a prior
    // error can't linger as a phantom subscriptionError and so resolvedIds
    // reflects only the NEW chunks (the safe direction — nothing is classified
    // "deleted" until a new chunk delivers). realTimeProducts is deliberately
    // NOT cleared: keep the last-known products so the drawer doesn't blank
    // during the resubscribe gap.
    setSubscriptionError(null);
    setResolvedIds(new Set());

    // Per-chunk state, merged on every delivery. Keyed by chunk index.
    const chunkResults = new Map<number, ProductDocument[]>();
    const chunkResolved = new Map<number, string[]>();
    const chunkErrors = new Map<number, Error>();

    // Push resolvedIds state from the current chunkResolved map. Split out so
    // the error path can re-derive it WITHOUT re-setting products (retention).
    const recomputeResolved = () => {
      const resolved = new Set<string>();
      chunkResolved.forEach((idList) => idList.forEach((id) => resolved.add(id)));
      setResolvedIds(resolved);
    };

    const recomputeProducts = () => {
      // Last-write-per-id merge across chunks. Ids are disjoint across chunks,
      // so there is no real conflict; the Map also de-dupes defensively.
      const merged = new Map<string, ProductDocument>();
      chunkResults.forEach((docs) => docs.forEach((d) => merged.set(d.id, d)));
      setProducts(Array.from(merged.values()));
      recomputeResolved();
    };

    const recomputeError = () => {
      const first = chunkErrors.values().next();
      setSubscriptionError(first.done ? null : first.value);
    };

    const unsubscribers = chunks.map((chunkIds, chunkIndex) =>
      onSnapshot(
        getProductsByIdQuery(chunkIds),
        (snapshot) => {
          chunkResults.set(
            chunkIndex,
            snapshot.docs.map((doc) => doc.data())
          );
          // A successful delivery marks this chunk's queried ids as resolved
          // and clears any prior error for the chunk.
          chunkResolved.set(chunkIndex, chunkIds);
          if (chunkErrors.delete(chunkIndex)) recomputeError();
          recomputeProducts();
        },
        (err) => {
          const errorCode =
            typeof (err as { code?: unknown })?.code === 'string'
              ? (err as { code: string }).code
              : 'unknown';
          // E2 (TASK-1323 §4): surface the previously-swallowed snapshot error.
          // SINGLE log via the wrapped logError (Sentry captureException + GCP
          // Error Reporting, with triage context and the original via `cause`),
          // plus the GA4 health event for volume. Now that chunking has landed
          // (PR-2), chunk_index is populated — it was intentionally absent in
          // PR-1's pre-chunk version.
          const wrapped = new Error(
            `[useRealtimeProducts] snapshot error (code=${errorCode}, idsCount=${ids.length}, chunkIndex=${chunkIndex}, chunkSize=${chunkIds.length})`
          );
          (wrapped as Error & { cause?: unknown }).cause = err;
          logError(wrapped);
          emitCartTelemetry(CART_TELEMETRY_EVENTS.REALTIME_SNAPSHOT_ERROR, {
            ids_count: ids.length,
            error_code: errorCode,
            chunk_index: chunkIndex,
          });

          chunkErrors.set(
            chunkIndex,
            err instanceof Error ? err : new Error(String(err))
          );
          // Un-resolve this chunk: an errored chunk must never let a consumer
          // misclassify a still-unknown product as deleted — even if the chunk
          // had previously delivered (a delivered-then-errored chunk left its
          // ids marked resolved in state without this re-derive). Products from
          // its last delivery are deliberately retained (chunkResults is
          // untouched) so the drawer doesn't blank; only resolvedIds drops it.
          chunkResolved.delete(chunkIndex);
          recomputeResolved();
          recomputeError();
        }
      )
    );

    return () => {
      unsubscribers.forEach((u) => u());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idsKey]);

  return {
    realTimeProducts,
    resolvedIds,
    subscriptionError,
  };
};

export default useRealTimeProducts;
